import { useEffect, useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import MypageMenu from '../../common/MypageMenu';
import '../../common/Mypage.scss';
import { userDataAtom } from '../../../../AtomStore';
import {
  fetchAiSermonList,
  deleteAiSermonItem,
  formatAiSermonDate,
  AI_SERMON_PREVIEW_LENGTH,
  AI_SERMON_TYPE_LABELS,
  type AiSermonItem,
} from '../../common/aiSermonApi';
import AiSermonGroupCard from './AiSermonGroupCard';

type ListCard =
  | { kind: 'single'; item: AiSermonItem }
  | { kind: 'group'; groupId: string; items: AiSermonItem[] };

function buildCards(list: AiSermonItem[]): ListCard[] {
  const cards: ListCard[] = [];
  const groupIndexByGroupId = new Map<string, number>();

  for (const item of list) {
    if (!item.groupId) {
      cards.push({ kind: 'single', item });
      continue;
    }
    const existingIndex = groupIndexByGroupId.get(item.groupId);
    if (existingIndex === undefined) {
      groupIndexByGroupId.set(item.groupId, cards.length);
      cards.push({ kind: 'group', groupId: item.groupId, items: [item] });
    } else {
      const card = cards[existingIndex];
      if (card.kind === 'group') card.items.push(item);
    }
  }

  return cards;
}

export default function AiSermonManage() {
  const userData = useAtomValue(userDataAtom);
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState<AiSermonItem[]>([]);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await fetchAiSermonList(userData.userAccount || '');
        if (!cancelled) setList(data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userData.userAccount]);

  const cards = useMemo(() => buildCards(list), [list]);

  const handleDelete = async (id: number) => {
    if (!window.confirm('이 생성 결과를 삭제하시겠습니까?')) return;
    const ok = await deleteAiSermonItem(id, userData.userAccount || '');
    if (ok) {
      setList((prev) => prev.filter((item) => item.id !== id));
    } else {
      alert('삭제에 실패했습니다.');
    }
  };

  const handleDeleteGroup = (ids: number[]) => {
    setList((prev) => prev.filter((item) => !ids.includes(item.id)));
  };

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      alert('복사되었습니다.');
    } catch {
      alert('복사에 실패했습니다.');
    }
  };

  return (
    <div className="mypage">
      <div className="inner">
        <MypageMenu />
        <div className="subpage__main">
          <div className="mypage-panel mypage-section-card">
            <div className="mypage-section-head">
              <div>
                <h2 className="mypage-section-title">AI설교지원 관리</h2>
                <p className="mypage-section-copy">
                  설교지원에서 생성한 설교요약·QT묵상·소그룹나눔지 결과를 확인하고 삭제할 수 있습니다.
                </p>
              </div>
            </div>

            {loading ? (
              <div className="noPosts">
                <p>불러오는 중입니다...</p>
              </div>
            ) : cards.length > 0 ? (
              <div className="mypage-scrap-list">
                {cards.map((card) => {
                  if (card.kind === 'group') {
                    return (
                      <AiSermonGroupCard
                        key={card.groupId}
                        items={card.items}
                        userAccount={userData.userAccount || ''}
                        onDeleteGroup={handleDeleteGroup}
                      />
                    );
                  }

                  const item = card.item;
                  const { date, time } = formatAiSermonDate(item.createdAt);
                  const isExpanded = expandedId === item.id;
                  const isLong = item.resultContent.length > AI_SERMON_PREVIEW_LENGTH;
                  const displayText = isExpanded || !isLong
                    ? item.resultContent
                    : `${item.resultContent.slice(0, AI_SERMON_PREVIEW_LENGTH)}...`;

                  return (
                    <article key={item.id} className="mypage-scrap-item">
                      <div className="mypage-row-between">
                        <div className="mypage-tag-row">
                          <span className="mypage-tag mypage-tag--sky">{AI_SERMON_TYPE_LABELS[item.type] || item.type}</span>
                        </div>
                        <span className="mypage-meta">
                          <span>{date}</span>
                          {time ? <span>{time}</span> : null}
                        </span>
                      </div>
                      <p style={{ whiteSpace: 'pre-wrap', marginTop: 10, lineHeight: 1.8 }}>{displayText}</p>
                      <div className="mypage-row-between" style={{ marginTop: 12 }}>
                        {isLong ? (
                          <button
                            type="button"
                            className="mypage-btn mypage-btn--outline"
                            onClick={() => setExpandedId(isExpanded ? null : item.id)}
                          >
                            {isExpanded ? '접기' : '전체보기'}
                          </button>
                        ) : <span />}
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button
                            type="button"
                            className="mypage-btn mypage-btn--outline"
                            onClick={() => handleCopy(item.resultContent)}
                          >
                            복사
                          </button>
                          <button
                            type="button"
                            className="mypage-btn mypage-btn--outline"
                            onClick={() => handleDelete(item.id)}
                          >
                            삭제
                          </button>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : (
              <div className="noPosts">
                <p>생성한 AI설교지원 결과가 없습니다.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
